import React from 'react';

/**
 * Page-Maker product info banner shown above the upload area when the
 * current order is flagged as a multi-design tiled page (e.g., Temporary
 * Tattoo Pages, Vinyl Sticker Pages). Explains the auto-tiling behavior
 * and renders a live sheet grid preview where each cell's hue maps to one
 * of the uploaded designs (round-robin).
 *
 * Props:
 *   tiles       — { total, cols, rows } from `calculateTilesPerSheet`,
 *                  or null if size/shape isn't known yet
 *   files       — array of File objects the user has selected
 */
export const PageMakerBanner = ({ tiles, files }) => (
  <div
    className="mb-6 p-5 rounded-lg border-2 border-[#4A7C59] bg-gradient-to-br from-green-50 to-emerald-50"
    data-testid="page-maker-banner"
  >
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-10 h-10 bg-[#4A7C59] rounded-full flex items-center justify-center">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-[#2E5A3D] text-lg mb-1" data-testid="page-maker-title">
          Page-Maker Enabled — Upload Multiple Designs
        </h3>
        <p className="text-sm text-[#3B5F46] leading-relaxed">
          This product supports <strong>multiple different designs on a single page</strong>.
          Upload as many designs as you like and we&rsquo;ll auto-tile them across each sheet.
          Each design will be printed at your selected size with a magenta die-line cutout.
        </p>
        {tiles && (
          <div
            className="mt-3 inline-flex items-center gap-4 text-sm font-medium text-[#2E5A3D] bg-white/70 rounded-md px-3 py-2 border border-[#4A7C59]/30"
            data-testid="page-maker-tile-info"
          >
            <span>
              <strong>{tiles.total}</strong> stickers per sheet
              <span className="text-[#3B5F46]/70">
                {' '}({tiles.cols} × {tiles.rows} grid)
              </span>
            </span>
            <span className="text-[#3B5F46]/70">
              {files.length > 0 ? `${files.length} design${files.length > 1 ? 's' : ''} added` : 'No designs yet'}
            </span>
          </div>
        )}
        {tiles && (
          <div
            className="mt-3 inline-block bg-white rounded-md p-2 border border-[#4A7C59]/30"
            data-testid="page-maker-grid-preview"
          >
            <div
              className="grid gap-[2px]"
              style={{
                gridTemplateColumns: `repeat(${tiles.cols}, 10px)`,
                gridTemplateRows: `repeat(${tiles.rows}, 10px)`,
              }}
            >
              {Array.from({ length: tiles.total }).map((_, i) => {
                const fileIdx = files.length > 0 ? i % files.length : 0;
                const hue = files.length > 0 ? (fileIdx * 47) % 360 : 0;
                const bg = files.length > 0 ? `hsl(${hue}, 55%, 70%)` : '#e5e7eb';
                return (
                  <div
                    // Fixed-length decorative grid — tiles are identical cells
                    // keyed by position; no reorder/insert/delete happens here.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`tile-${i}`}
                    style={{ backgroundColor: bg }}
                    className="rounded-[2px]"
                  />
                );
              })}
            </div>
            {files.length > 0 && (
              <div className="mt-1 text-[10px] text-[#3B5F46]/70 text-center">
                Each color = one of your designs (round-robin)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);
